import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import NavHeader from '../components/NavHeader';
import { getPendingApprovals, respondToApproval } from '../api';

function formatCurrency(amount, currency) {
  if (amount == null) return '';
  const num = parseFloat(amount);
  if (isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(num);
}

function formatDateTime(dt) {
  if (!dt) return '';
  return new Date(dt).toLocaleString();
}

function ApproverQueue() {
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const showToast = useCallback((message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadApprovals = useCallback(async () => {
    try {
      const res = await getPendingApprovals();
      setApprovals(res.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load pending approvals');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const handleApprove = async (requestId) => {
    setProcessingId(requestId);
    try {
      await respondToApproval(requestId, 'approved');
      showToast('Invoice approved', 'success');
      loadApprovals();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Approval failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectClick = (requestId) => {
    setRejectingId(requestId);
    setRejectNotes('');
  };

  const handleRejectConfirm = async (requestId) => {
    setProcessingId(requestId);
    try {
      await respondToApproval(requestId, 'rejected', rejectNotes || null);
      showToast('Invoice rejected', 'success');
      setRejectingId(null);
      setRejectNotes('');
      loadApprovals();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Rejection failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectCancel = () => {
    setRejectingId(null);
    setRejectNotes('');
  };

  if (loading) {
    return (
      <div className="board-loading">
        <div className="loading-spinner">Loading approvals...</div>
      </div>
    );
  }

  return (
    <div className="approver-queue-page">
      <NavHeader />

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
        </div>
      )}

      <div className="approver-queue-content">
        <h2 className="approver-queue-heading">Pending Approvals</h2>

        {error && <div className="error-message">{error}</div>}

        {approvals.length === 0 && !error ? (
          <div className="approver-queue-empty">
            <div className="approver-queue-empty-icon">&#10003;</div>
            <p>No pending approvals</p>
            <span>All caught up! Check back later.</span>
          </div>
        ) : (
          <div className="approval-cards">
            {approvals.map((item) => (
              <div
                className="approval-card"
                key={item.id}
                onClick={() => navigate(`/invoice/${item.invoice_id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="approval-card-header">
                  <span className="approval-vendor">{item.vendor_name || 'Unknown Vendor'}</span>
                  <span className="approval-amount">
                    {formatCurrency(item.invoice_amount || item.amount, item.invoice_currency || item.currency)}
                  </span>
                </div>
                <div className="approval-card-details">
                  {item.invoice_number && (
                    <div className="approval-detail-row">
                      <span className="approval-label">Invoice #</span>
                      <span>{item.invoice_number}</span>
                    </div>
                  )}
                  {item.project_name && (
                    <div className="approval-detail-row">
                      <span className="approval-label">Project</span>
                      <span>{item.project_name}</span>
                    </div>
                  )}
                  <div className="approval-detail-row">
                    <span className="approval-label">Requested</span>
                    <span>{formatDateTime(item.created_at)}</span>
                  </div>
                  {item.requester_name && (
                    <div className="approval-detail-row">
                      <span className="approval-label">Requested by</span>
                      <span>{item.requester_name}</span>
                    </div>
                  )}
                </div>
                <div className="approval-card-actions" onClick={(e) => e.stopPropagation()}>
                  {rejectingId === item.id ? (
                    <div className="rejection-form">
                      <input
                        type="text"
                        className="rejection-notes-input"
                        placeholder="Rejection reason (optional)"
                        value={rejectNotes}
                        onChange={(e) => setRejectNotes(e.target.value)}
                        autoFocus
                      />
                      <div className="rejection-form-buttons">
                        <button
                          className="btn btn-reject"
                          onClick={() => handleRejectConfirm(item.id)}
                          disabled={processingId === item.id}
                        >
                          {processingId === item.id ? 'Rejecting...' : 'Confirm Reject'}
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={handleRejectCancel}
                          disabled={processingId === item.id}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        className="btn btn-approve"
                        onClick={() => handleApprove(item.id)}
                        disabled={processingId === item.id}
                      >
                        {processingId === item.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        className="btn btn-reject"
                        onClick={() => handleRejectClick(item.id)}
                        disabled={processingId === item.id}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ApproverQueue;
